/**
 * 직원 상세 - 조직/직무 탭 (V62 안정화)
 *
 * 편집 가능 필드:
 * - roleIds: 역할 수정 (기존 체크박스 로직 유지)
 *
 * 편집 불가 필드 (BLOCKED):
 * - orgUnitId, positionId, titleId — 인사발령/전용 로직에서만 변경
 * - affiliationName — 인적사항 탭에서 수정
 */

import React, { useState } from 'react';
import { Briefcase, Edit2, Save, X } from 'lucide-react';
import { Employee } from '../../../../shared/types/employee';
import { getRoleLabels, getRoleLabel, ASSIGNABLE_ROLE_IDS } from '../../../../shared/constants/roles';
import { useEmployeeEditableFieldsMutations } from '../../../../shared/hooks/useEmployeeEditableFieldsMutations';

interface OrganizationTabProps {
  employee: Employee;
  canEdit: boolean;
  onRefresh: () => void;
}

export function OrganizationTab({ employee, canEdit, onRefresh }: OrganizationTabProps) {
  // ========== 역할 수정 (기존 로직 유지) ==========
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [editedRoleIds, setEditedRoleIds] = useState<string[]>(employee.roleIds || []);
  const { updateEditableFields, isLoading: roleLoading } = useEmployeeEditableFieldsMutations();

  const handleEditRole = () => {
    setEditedRoleIds(employee.roleIds || []);
    setIsEditingRole(true);
  };

  const handleCancelRole = () => {
    setEditedRoleIds(employee.roleIds || []);
    setIsEditingRole(false);
  };

  const handleSaveRole = async () => {
    const success = await updateEditableFields(employee.id, {
      roleIds: editedRoleIds,
    });
    if (success) {
      alert('역할이 수정되었습니다.');
      setIsEditingRole(false);
      onRefresh();
    }
  };

  const handleToggleRole = (roleId: string) => {
    if (editedRoleIds.includes(roleId)) {
      setEditedRoleIds(editedRoleIds.filter(id => id !== roleId));
    } else {
      setEditedRoleIds([...editedRoleIds, roleId]);
    }
  };

  return (
    <div style={containerStyle}>
      {/* 섹션 헤더 */}
      <div style={headerStyle}>
        <Briefcase size={20} style={{ color: 'var(--color-primary)' }} />
        <h3 style={titleStyle}>조직/직무</h3>
        {canEdit && !isEditingRole && (
          <button onClick={handleEditRole} style={{ ...editButtonStyle, marginLeft: 'auto' }}>
            <Edit2 size={16} />
            역할 수정
          </button>
        )}
        {isEditingRole && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button onClick={handleCancelRole} style={cancelButtonStyle} disabled={roleLoading}>
              <X size={16} />
              취소
            </button>
            <button onClick={handleSaveRole} style={saveButtonStyle} disabled={roleLoading}>
              <Save size={16} />
              {roleLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        )}
      </div>

      {/* 소속/부서/직위/직책 그리드 */}
      <div style={gridStyle}>
        <InfoItem label="소속" value={employee.affiliationName || '-'} hint="인적사항에서 수정" />
        <InfoItem label="부서" value={employee.orgUnitName || '-'} hint="인사발령에서 변경" />
        <InfoItem label="직위" value={employee.positionTitle || '-'} hint="인사발령에서 변경" />
        <InfoItem label="직책" value={employee.jobTitle || '-'} hint="인사발령에서 변경" />

        {/* 역할 (읽기) */}
        {!isEditingRole && (
          <div>
            <div style={labelStyle}>역할</div>
            <div style={valueStyle}>{getRoleLabels(employee.roleIds)}</div>
          </div>
        )}
      </div>

      {/* 역할 편집 모드 (기존 로직 유지) */}
      {isEditingRole && (
        <div style={{ marginTop: '12px' }}>
          <div style={labelStyle}>역할</div>
          <div style={roleCheckboxContainerStyle}>
            {ASSIGNABLE_ROLE_IDS.map((roleId) => (
              <label key={roleId} style={roleCheckboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={editedRoleIds.includes(roleId)}
                  onChange={() => handleToggleRole(roleId)}
                  style={{ marginRight: '8px' }}
                />
                {getRoleLabel(roleId)}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 공통 컴포넌트
// ============================================

interface InfoItemProps {
  label: string;
  value: string;
  hint?: string;
}

function InfoItem({ label, value, hint }: InfoItemProps) {
  return (
    <div>
      <div style={labelStyle}>
        {label}
        {hint && <span style={{ marginLeft: '6px', color: 'var(--color-gray-400)', fontSize: '11px' }}>({hint})</span>}
      </div>
      <div style={valueStyle}>{value}</div>
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const containerStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid var(--color-gray-200)',
};

const titleStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: '600',
  color: 'var(--color-gray-800)',
  margin: 0,
};

const editButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const saveButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-success)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const cancelButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '8px 16px',
  background: 'var(--color-gray-200)',
  color: 'var(--color-gray-700)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '12px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--color-gray-500)',
  marginBottom: '4px',
};

const valueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-gray-800)',
  fontWeight: '500',
};

const roleCheckboxContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginTop: '8px',
};

const roleCheckboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  cursor: 'pointer',
};
